import React, { useState, useEffect } from "react";
import {
  Popover,
  Box,
  Typography,
  IconButton,
  Button,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { Close, DoneAll } from "@mui/icons-material";
import { useInfiniteQuery, useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { QK } from "../../base/qk";
import {
  getAllNotifications,
  markAllNotificationsRead,
} from "../../api/services/notification";
import NotificationTabs from "./Notifications/NotificationTabs";
import NotificationList from "./Notifications/NotificationList";
import NotificationActionMenu from "./Notifications/NotificationActionMenu";

const NotificationPopover = ({
  open,
  anchorEl,
  onClose,
  onUnreadChange,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(0);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useInfiniteQuery(
    [QK.notifications],
    ({ pageParam = 1 }) => getAllNotifications(pageParam),
    {
      enabled: true,
      refetchInterval: 30000,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      getNextPageParam: (lastPage) => {
        if (lastPage.next) {
          const url = new URL(lastPage.next);
          const page = url.searchParams.get("page");
          return page ? parseInt(page) : null;
        }
        return null;
      },
      onSuccess: (data) => {
        const allNotifs = data?.pages?.flatMap((page) => page.results) || [];
        const count = allNotifs.filter((n) => !n.is_read).length;
        setUnreadCount(count);
      },
    }
  );

  useEffect(() => {
    if (open) {
      refetch();
    }
  }, [open, refetch]);

  useEffect(() => {
    if (onUnreadChange) {
      onUnreadChange(unreadCount > 0);
    }
  }, [unreadCount, onUnreadChange]);

  const allNotifications = data?.pages?.flatMap((page) => page.results) || [];
  const totalCount = data?.pages?.[0]?.count || 0;

  const markAllReadMutation = useMutation(() => markAllNotificationsRead(), {
    onSuccess: () => {
      queryClient.invalidateQueries([QK.notifications]);
      refetch();
      setUnreadCount(0);
    },
  });

  const handleActionMenuOpen = (event, notification) => {
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
    setSelectedNotification(notification);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setSelectedNotification(null);
  };

  const handleMarkAllRead = () => {
    markAllReadMutation.mutate();
  };

  const filteredNotifications =
    activeTab === 0
      ? allNotifications
      : allNotifications.filter((n) => !n.is_read);

  return (
    <>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          elevation: 4,
          sx: {
            width: 420,
            maxHeight: 480,
            borderRadius: 3,
            mt: 1.5,
            overflow: "hidden",
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
      >
        <Box sx={{ p: 1.5, borderBottom: "1px solid #e2e8f0" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight={700}
              fontSize="0.95rem"
            >
              Recent Activities
            </Typography>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {unreadCount > 0 && (
                <Button
                  size="small"
                  startIcon={
                    markAllReadMutation.isLoading ? (
                      <CircularProgress size={12} />
                    ) : (
                      <DoneAll sx={{ fontSize: 14 }} />
                    )
                  }
                  onClick={handleMarkAllRead}
                  disabled={markAllReadMutation.isLoading}
                  sx={{
                    fontSize: "0.6rem",
                    fontWeight: 600,
                    textTransform: "none",
                    color: theme.palette.primary.main,
                    minHeight: 28,
                    px: 1,
                  }}
                >
                  Mark all read
                </Button>
              )}
              <IconButton size="small" onClick={onClose} sx={{ p: 0.5 }}>
                <Close fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          <NotificationTabs
            activeTab={activeTab}
            onTabChange={setActiveTab}
            totalCount={totalCount}
            unreadCount={unreadCount}
          />
        </Box>

        <NotificationList
          notifications={filteredNotifications}
          isLoading={isLoading}
          activeTab={activeTab}
          onNotificationClick={(notification) => {
            if (notification.link) {
              navigate(notification.link);
            }
            onClose();
          }}
          onActionMenuOpen={handleActionMenuOpen}
          onLoadMore={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onViewAll={() => {
            onClose();
            navigate("/notifications");
          }}
          hasAnyNotifications={allNotifications.length > 0}
        />
      </Popover>

      <NotificationActionMenu
        anchorEl={actionMenuAnchor}
        onClose={handleActionMenuClose}
        notification={selectedNotification}
        onRefetch={refetch}
      />
    </>
  );
};

export default NotificationPopover;